/**
 * Event contract between the main desktop window and the floating Benny
 * companion window. The companion is a dumb renderer: the main window owns
 * all app state (timer, assistant, notifications) and streams facts to the
 * companion, which decides how Benny reacts. The companion sends user
 * intents (open chat, navigate) back to the main window.
 */

export const BENNY_COMPANION_EVENTS = {
  /** companion → main: webview mounted (or reloaded) and wants a snapshot */
  ready: 'benny-companion-ready',
  /** main → companion: full activity snapshot (timer + assistant state) */
  sync: 'benny-companion-sync',
  /** main → companion: a notification to surface as a speech bubble */
  notification: 'benny-companion-notification',
  /** companion → main: focus the main window and optionally navigate */
  navigate: 'benny-companion-navigate',
  /**
   * companion → main: discard the active recording. Handled in the main
   * window because the upload pipeline (and its discarded-ids skip list)
   * lives there — the companion can stop a recording directly via Rust, but
   * only the main window can mark it as never-to-upload.
   */
  discardRecording: 'benny-companion-discard-recording',
  /**
   * main → companion: acknowledgment that the active recording was marked
   * discarded (never-upload) and stopped. The companion treats a discard as
   * successful only when this arrives.
   */
  recordingDiscarded: 'benny-companion-recording-discarded',
  /** companion → main: open the Benny chat panel */
  openChat: 'open-benny-chat',
  /**
   * companion → main: the user switched hub views. The main window persists
   * it to the synced user preferences so the choice survives a relaunch and
   * follows the user across devices.
   */
  viewChanged: 'benny-companion-view-changed',
  /** rust → all: companion was disabled (window closed / setting off) */
  disabled: 'benny-companion-disabled',
} as const

export interface CompanionTimerState {
  running: boolean
  isPaused: boolean
  /** Elapsed seconds at the moment this snapshot was emitted. */
  elapsedSeconds: number
  /** Timer description / linked module name, if any. */
  label?: string | null
  /** Main-window route to open when the timer chip is clicked. */
  route?: string | null
  /** Ids of the modules this timer is linked to (primary + linked). */
  moduleIds?: string[]
}

/**
 * Snapshot of what the user is looking at in the main window. Mirrors the
 * ingredients BennyChat uses to build its aiContext/pageContext, so the
 * companion chat can reference and act on "this task/project/page" exactly
 * like the in-app chat.
 */
export interface CompanionPageContext {
  pathname: string
  search: string
  hash: string
  title: string
  params: Record<string, string | undefined>
}

export interface CompanionRecordingState {
  active: boolean
  isPaused: boolean
  /** Elapsed seconds at the moment this snapshot was emitted. */
  elapsedSeconds: number
}

/**
 * A favorited record, streamed from the main window's live favorites store so
 * the companion's Favorites view mirrors the dashboard widget in realtime.
 * `type` is an uppercased ModuleTypeEnum (ENTITY, PROJECT, TASK, ...).
 */
export interface CompanionFavorite {
  id: string
  name: string
  type: string
  parentName?: string
  identifier?: string
}

export interface CompanionSyncPayload {
  timer: CompanionTimerState | null
  /** Meeting recording in progress (drives the REC chip). */
  recording?: CompanionRecordingState | null
  /** True while the Benny assistant is streaming a response. */
  assistantThinking: boolean
  /** Current route context of the main window (null when unknown). */
  pageContext?: CompanionPageContext | null
  /** Active organization — lets the companion follow org switches. */
  organization?: { id: string; slug: string } | null
  /**
   * Last hub view persisted in the user's synced preferences. `null` means
   * the user has never chosen one (the companion shows its launcher).
   */
  companionView?: 'chat' | 'upcoming' | 'favorites' | null
  /** The user's favorited records, kept live from the main window's store. */
  favorites?: CompanionFavorite[]
}

export interface CompanionNotificationPayload {
  id: string
  title: string
  message?: string
  /** Main-window route to open when the bubble is clicked. */
  route?: string
  priority?: string
  createdAt?: string
}

export interface CompanionNavigatePayload {
  /** When omitted, the main window is focused without navigating. */
  route?: string
}

export type CompanionView = 'chat' | 'upcoming' | 'favorites'

export interface CompanionViewChangedPayload {
  view: CompanionView
}
